import { Link } from 'react-router-dom';
import { 
  ArrowRight, Microscope, DollarSign, Globe, CheckCircle,
  BookOpen, Users, Award, Target, Lightbulb, Heart,
  Clock, Shield, GraduationCap
} from 'lucide-react';
import { SEO, pageSEO } from '../components/SEO';

const whyChooseUs = [
  {
    icon: Microscope,
    title: 'Research Hub in Africa',
    description: 'Research is central to the CIU academic journey. We instill a strong research culture in our students to address local and global challenges.',
    highlights: [
      'CEPRES International Journal of Science and Innovative Studies',
      'Student research projects addressing real-world problems',
      'Faculty-led research initiatives',
      'Partnerships with international research institutions'
    ],
  },
  {
    icon: DollarSign,
    title: 'Quality and Affordable Education',
    description: 'CIU ensures students apply learning while still in the classroom, producing graduates with proven competencies.',
    highlights: [
      'Competitive tuition rates',
      'Flexible payment plans',
      'Scholarship opportunities',
      'Graduate debt-free with marketable skills'
    ],
  },
  {
    icon: Globe,
    title: 'Accessibility and Availability',
    description: 'With a modern Learning Management System (LMS), CIU delivers education that is accessible regardless of location, schedule, financial constraints, or disability.',
    highlights: [
      'Online and blended learning options',
      'Flexible scheduling for working professionals',
      'Support for students with disabilities',
      'Multiple intake periods throughout the year'
    ],
  },
];

const programTypes = [
  {
    title: 'Undergraduate Diploma & Certificate Programs',
    description: 'Practical, real-world skills that prepare students for immediate employment. These professional credentials empower individuals to build sustainable careers.',
    icon: Award,
  },
  {
    title: 'Postgraduate Diploma & Certificate Programs',
    description: 'Achieve postgraduate qualifications without the financial or time commitment of a full Master\'s degree. Industry-relevant, practical knowledge for career advancement.',
    icon: GraduationCap,
  },
  {
    title: 'Undergraduate Degree Programs',
    description: 'Emphasizing experiential learning, cognitive development, and results-driven education. Graduate with a strong foundation in theory and real-world problem-solving.',
    icon: BookOpen,
  },
];

const stats = [
  { number: '19+', label: 'Degree Programs' },
  { number: '17', label: 'Vocational Programs' },
  { number: '1000+', label: 'Graduates' },
  { number: '10+', label: 'Years of Excellence' },
];

export default function WhyCIU() {
  return (
    <div data-testid="why-ciu-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.whyCIU} url="/why-ciu" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0D4A2E] pattern-overlay">
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Why Choose <span className="text-[#C5A028]">CIU?</span>
          </h1>
          <p className="text-white/70 text-xl max-w-2xl">
            Discover what makes CEPRES International University the right choice 
            for your academic and professional journey.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-[#C5A028]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-bold text-[#0D4A2E]">{stat.number}</div>
                <div className="text-[#0D4A2E]/70 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Target className="w-16 h-16 text-[#C5A028] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-[#0D4A2E] mb-6">Our Mission</h2>
            <p className="text-[#666666] text-lg leading-relaxed">
              CEPRES International University supports your career ambitions by aligning your goals 
              with practical education, accredited programs, and real-world relevance. Our hybrid and 
              cost-effective learning model gives students the flexibility to graduate debt-free, 
              equipped with the skills and knowledge required to become industry leaders.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Main Features */}
      <section data-testid="why-choose-features" className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D4A2E] mb-4 text-center">What Sets Us Apart</h2>
          <p className="text-[#666666] mb-12 text-center max-w-2xl mx-auto">
            Three pillars that define the CIU experience
          </p>

          <div className="space-y-12">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-[#0D4A2E] flex items-center justify-center">
                      <item.icon className="w-7 h-7 text-[#C5A028]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0D4A2E]">{item.title}</h3>
                  </div>
                  <p className="text-[#666666] mb-6 leading-relaxed">{item.description}</p>
                  <ul className="space-y-3">
                    {item.highlights.map((highlight, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#C5A028] flex-shrink-0 mt-0.5" />
                        <span className="text-[#666666]">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`bg-white p-8 shadow-lg ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <item.icon className="w-24 h-24 text-[#0D4A2E]/10 mx-auto" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Types */}
      <section className="py-20 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl font-bold mb-4 text-center">Program <span className="text-[#C5A028]">Offerings</span></h2>
          <p className="text-white/70 mb-12 text-center max-w-2xl mx-auto">
            Multiple pathways to achieve your educational and career goals
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {programTypes.map((program, index) => (
              <div 
                key={index}
                className="bg-white/5 border border-white/10 p-8 hover:bg-white/10 transition-colors duration-300"
              >
                <program.icon className="w-12 h-12 text-[#C5A028] mb-4" />
                <h3 className="text-xl font-bold mb-3">{program.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{program.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link 
              to="/programs"
              className="inline-flex items-center gap-2 bg-white text-[#0D4A2E] px-8 py-4 font-bold hover:bg-[#F5F5F5] transition-colors duration-200 mr-4"
            >
              <ArrowRight className="w-5 h-5" />
              DEGREE PROGRAMS
            </Link>
            <Link 
              to="/vocational"
              className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
            >
              VOCATIONAL PROGRAMS
            </Link>
          </div>
        </div>
      </section>

      {/* Accreditation Highlight */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Shield className="w-12 h-12 text-[#C5A028] mb-4" />
              <h2 className="text-3xl font-bold text-[#0D4A2E] mb-6">
                Accredited & <span className="text-[#C5A028]">Recognized</span>
              </h2>
              <p className="text-[#666666] mb-6 leading-relaxed">
                Our academic offerings are fully accredited by the National Commission on Higher 
                Education (NCHE) of the Republic of Liberia. CIU is also a member of the 
                International Academic and Management Association (IAMA) and maintains partnerships 
                with multiple universities and organizations.
              </p>
              <Link 
                to="/accreditation"
                className="inline-flex items-center gap-2 text-[#C5A028] font-semibold hover:gap-4 transition-all duration-300"
              >
                <ArrowRight className="w-5 h-5" />
                VIEW ACCREDITATIONS
              </Link>
            </div>
            <div className="bg-[#F5F5F5] p-8">
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <Award className="w-10 h-10 text-[#0D4A2E]" />
                  <div>
                    <h4 className="font-bold text-[#0D4A2E]">NCHE Accredited</h4>
                    <p className="text-[#666666] text-sm">National Commission on Higher Education</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Globe className="w-10 h-10 text-[#0D4A2E]" />
                  <div>
                    <h4 className="font-bold text-[#0D4A2E]">IAMA Member</h4>
                    <p className="text-[#666666] text-sm">International Academic and Management Association</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Users className="w-10 h-10 text-[#0D4A2E]" />
                  <div>
                    <h4 className="font-bold text-[#0D4A2E]">Global Partnerships</h4>
                    <p className="text-[#666666] text-sm">Collaborations with universities worldwide</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#C5A028]">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#0D4A2E] mb-4">Ready to Transform Your Future?</h2>
          <p className="text-[#0D4A2E]/80 mb-8">
            Join thousands of students who have chosen CIU for quality, affordable, and accessible education.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://cnte.ciu.edu.lr/apply"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#0D4A2E] text-white px-8 py-4 font-bold hover:bg-[#082D1C] transition-colors duration-200"
            >
              <ArrowRight className="w-5 h-5" />
              APPLY NOW
            </a>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-[#0D4A2E] px-8 py-4 font-bold hover:bg-[#F5F5F5] transition-colors duration-200"
            >
              CONTACT US
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
